import { combineReducers } from 'redux';

import { PartnerDetails as Partner } from '@/components/b2b/shared/types';

// Reducers
import { appLoadedReducer } from './appLoaded/reducers';
import { AppLoaded } from './appLoaded/types';
import { browserLanguageReducer } from './browserLanguage/reducers';
import { BrowserLanguage } from './browserLanguage/types';
import { campaignReducer } from './campaign/reducers';
import { Campaign } from './campaign/types';
import { campaignConfigReducer } from './campaignConfig/reducers';
import { CampaignConfig } from './campaignConfig/types';
import { complianceLevelReducer } from './complianceLevel/reducers';
import { ComplianceLevel } from './complianceLevel/types';
import { deviceReducer } from './device/reducers';
import { CallToAction, Device } from './device/types';
import { guideReducer } from './guide/reducers';
import { Guide } from './guide/types';
import { localeReducer } from './locale/reducers';
import { Locale } from './locale/types';
import { notificationReducer } from './notification/reducers';
import { Notification } from './notification/types';
import { partnerReducer } from './partner/reducers';
import { pricesReducer } from './prices/reducers';
import { Prices } from './prices/types';
import { programReducer } from './program/reducers';
import { Program } from './program/types';
import { promoOfferReducer } from './promoOffer/reducers';
import { PromoOfferState } from './promoOffer/types';
import { promoOfferEnabledReducer } from './promoOfferEnabled/reducers';
import { PromoOfferEnabledState } from './promoOfferEnabled/types';
import { purchaseParamsReducer } from './purchaseParams/reducers';
import { PurchaseParams } from './purchaseParams/types';
import { recaptchaVisibleReducer } from './recaptchaVisible/reducers';
import { RecaptchaVisible } from './recaptchaVisible/types';
import { serverSideReducer } from './serverSide/reducers';
import { ServerSide } from './serverSide/types';
import { themeReducer } from './theme/reducers';
import { Theme } from './theme/types';
import { userReducer } from './user/reducers';
import { User } from './user/types';

export type AppState = {
	appLoaded: AppLoaded;
	browserLanguage: BrowserLanguage | null;
	campaign: Campaign;
	campaignConfig: CampaignConfig | null;
	complianceLevel: ComplianceLevel;
	device: Device;
	guide: Guide | null;
	locale: Locale | null;
	notification: Notification | null;
	partner: Partner | null;
	prices: Prices;
	program: Program | null;
	promoOffer: PromoOfferState;
	promoOfferEnabled: PromoOfferEnabledState;
	purchaseParams: PurchaseParams;
	recaptchaVisible: RecaptchaVisible;
	serverSide: ServerSide;
	theme: Theme;
	user: User | null;
};

export type AppInitialState = {
	appLoaded: AppState['appLoaded'];
	browserLanguage: AppState['browserLanguage'];
	complianceLevel: AppState['complianceLevel'];
	device: AppState['device'];
	serverSide: AppState['serverSide'];
	prices: AppState['prices'];
	user: AppState['user'];
	locale: AppState['locale'];
	campaign?: AppState['campaign'];
	campaignConfig?: AppState['campaignConfig'];
	guide?: AppState['guide'];
	notification?: AppState['notification'];
	partner?: AppState['partner'];
	program?: AppState['program'];
	promoOffer?: AppState['promoOffer'];
	promoOfferEnabled?: AppState['promoOfferEnabled'];
	purchaseParams?: AppState['purchaseParams'];
	recaptchaVisible?: AppState['recaptchaVisible'];
	theme?: AppState['theme'];
};

export type InitialServerState = {
	appLoaded: AppState['appLoaded'];
	browserLanguage: AppState['browserLanguage'];
	complianceLevel: AppState['complianceLevel'];
	device?: AppState['device'];
	prices: AppState['prices'];
	purchaseParams: AppState['purchaseParams'];
	serverSide: AppState['serverSide'];
	// Omitted server-side; the theme slice's own default matches this literal
	// exactly, so falling through to the reducer default is equivalent.
	theme?: AppState['theme'];
	user: AppState['user'];
	locale: AppState['locale'];
	program: AppState['program'];
	guide: AppState['guide'];
	promoOffer?: AppState['promoOffer'];
	promoOfferEnabled?: AppState['promoOfferEnabled'];
};

export type OAuthCustomDeviceCall = {
	call_to_action?: CallToAction | null;
	user?: User | null;
};

export const rootReducer = combineReducers({
	appLoaded: appLoadedReducer,
	browserLanguage: browserLanguageReducer,
	campaign: campaignReducer,
	campaignConfig: campaignConfigReducer,
	complianceLevel: complianceLevelReducer,
	device: deviceReducer,
	guide: guideReducer,
	locale: localeReducer,
	notification: notificationReducer,
	partner: partnerReducer,
	prices: pricesReducer,
	program: programReducer,
	promoOffer: promoOfferReducer,
	promoOfferEnabled: promoOfferEnabledReducer,
	purchaseParams: purchaseParamsReducer,
	recaptchaVisible: recaptchaVisibleReducer,
	serverSide: serverSideReducer,
	theme: themeReducer,
	user: userReducer,
});
